import React, {useMemo, useState} from "react";
import { Badge, Empty, Segmented, Tag, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAllExchanges } from "../../../store/exchangeActiveSlice";
import { useExchangeActiveWs } from "./hooks/useExchangeActiveWs";

const { Title, Text } = Typography;

const FILTERS = {
  ACTIVE: "ACTIVE",
  COMPLETED: "COMPLETED",
  DISPUTE: "DISPUTE",
  CANCELLED: "CANCELLED",
};

function normalizeStatus(s) {
  return String(s || "").toUpperCase();
}

function isActiveStatus(status) {
  const s = normalizeStatus(status);
  return ["CREATED", "IN_PROGRESS"].includes(s);
}

function isClosedStatus(status) {
  const s = normalizeStatus(status);
  return ["COMPLETED"].includes(s);
}

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function statusTag(status) {
  const s = normalizeStatus(status);

  if (s === "DISPUTE") return <Tag color="red">DISPUTE</Tag>;
  if (s === "CANCELLED") return <Tag>Cancelled</Tag>;
  if (isClosedStatus(s)) return <Tag color="green">Done</Tag>;
  if (s === "IN_PROGRESS") return <Tag color="gold">In progress</Tag>;
  return <Tag color="blue">{s}</Tag>;
}

function formatMoneyLike(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}


function chip(label, value) {
  if (!value) return null;
  return (
    <div className="rounded-[12px] bg-[#F3F6FF] px-3 py-2 text-[12px] text-[#0B1B3A]">
      <span className="text-black/45">{label}:</span>{" "}
      <span className="font-extrabold">{value}</span>
    </div>
  );
}

export default function ExchangeMy() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState(FILTERS.ACTIVE);

  // Refresh all filters on page open (runs once on mount if WS already connected)
  useExchangeActiveWs({ enabled: true });

  // Data comes from Redux — kept in sync globally by ExchangeNotifierBar's useExchangeActiveWs
  const allItems = useSelector(selectAllExchanges);

  // Map Redux shape → display shape (Redux items are already adapted by slice)
  const items = useMemo(() => {
    return allItems.map((x) => ({
      id: x.id,
      exchange: x.exchange,
      code: x.code,
      status: x.status,
      created_at: x.created_at,
      amount_from: x.amount_from,
      amount_to: x.amount_to,
      currency_from: x.from?.currency ?? "—",
      currency_to: x.to?.currency ?? "—",
      method_from: x.from?.method ?? "—",
      method_to: x.to?.method ?? "—",
      payout_type: x.payout_type,
      payout_detail: x.payout_detail,
      buy_rate: x.buy_rate,
      sell_rate: x.sell_rate,
    }));
  }, [allItems]);

  const counts = useMemo(() => {
    const c = { active: 0, closed: 0, dispute: 0, cancelled: 0 };
    for (const x of items) {
      const s = normalizeStatus(x.status);
      if (isActiveStatus(s)) c.active++;
      else if (isClosedStatus(s)) c.closed++;
      else if (s === "DISPUTE") c.dispute++;
      else if (s === "CANCELLED") c.cancelled++;
    }
    return c;
  }, [items]);

  const data = useMemo(() => {
    return items
      .filter((x) => {
        const s = normalizeStatus(x.status);
        if (filter === FILTERS.ACTIVE) return isActiveStatus(s);
        if (filter === FILTERS.COMPLETED) return isClosedStatus(s);
        if (filter === FILTERS.DISPUTE) return s === "DISPUTE";
        if (filter === FILTERS.CANCELLED) return s === "CANCELLED";
        return true;
      })
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [items, filter]);

  return (
    <div className="w-full">
      <style>{`
        .exh-seg-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .exh-seg-scroll::-webkit-scrollbar { height: 6px; }
        .exh-seg-scroll::-webkit-scrollbar-thumb { background: rgba(2,6,23,0.18); border-radius: 999px; }
        .exh-seg-scroll::-webkit-scrollbar-track { background: transparent; }
        .exh-seg-scroll .ant-segmented { min-width: max-content; }
        @media (max-width: 640px) {
          .exh-tag-tight .ant-tag { padding: 0 8px; }
        }
      `}</style>

      <div className="mx-auto w-full max-w-[1160px] px-3 sm:px-5 py-5 sm:py-7">
        <div className="rounded-[18px] sm:rounded-[22px] bg-[#F5F7FF] p-2 sm:p-4">
          <div className="rounded-[16px] sm:rounded-[18px] bg-white border border-black/5 shadow-sm">
            {/* Header */}
            <div className="px-4 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-black/5">
              <div className="flex items-start justify-between gap-3 sm:gap-4 flex-wrap">
                <div className="min-w-0">
                  <Title level={2} className="!mb-1 !text-[22px] sm:!text-[28px]">
                    Мои обмены
                  </Title>
                  <div className="text-[12px] sm:text-[13px] text-black/60">
                    Нажмите на карточку, чтобы открыть управление обменом.
                  </div>
                </div>

                <div className="text-left sm:text-right">
                  <div className="text-[11px] sm:text-[12px] text-black/50">
                    В выбранном фильтре
                  </div>
                  <div className="text-[20px] sm:text-[22px] font-extrabold leading-[24px] sm:leading-[26px]">
                    {data.length}
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="exh-seg-scroll w-full sm:w-auto">
                  <Segmented
                    size="large"
                    value={filter}
                    onChange={setFilter}
                    options={[
                      {
                        label: (
                          <span className="whitespace-nowrap">
                            Активные <Badge count={counts.active} offset={[6, -2]} />
                          </span>
                        ),
                        value: FILTERS.ACTIVE,
                      },
                      {
                        label: (
                          <span className="whitespace-nowrap">
                            Закрытые <Badge count={counts.closed} offset={[6, -2]} />
                          </span>
                        ),
                        value: FILTERS.COMPLETED,
                      },
                      {
                        label: (
                          <span className="whitespace-nowrap">
                            Спор <Badge count={counts.dispute} offset={[6, -2]} />
                          </span>
                        ),
                        value: FILTERS.DISPUTE,
                      },
                      {
                        label: (
                          <span className="whitespace-nowrap">
                            Отменённые <Badge count={counts.cancelled} offset={[6, -2]} />
                          </span>
                        ),
                        value: FILTERS.CANCELLED,
                      },
                    ]}
                  />
                </div>

                <Text type="secondary" className="text-[11px] sm:text-[12px]">
                  Последние сверху
                </Text>
              </div>
            </div>

            {/* Body */}
            <div className="px-3 sm:px-6 py-4 sm:py-5">
              {data.length === 0 ? (
                <div className="py-10">
                  <Empty description="Нет заявок по выбранному фильтру" />
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:gap-3">
                  {data.map((x) => (
                    <button
                      key={x.id}
                      type="button"
                      onClick={() => navigate(`/exchange/flow/${x.id}`)}
                      className={[
                        "w-full text-left",
                        "rounded-[14px] sm:rounded-[16px] border border-black/5 bg-white",
                        "px-3 sm:px-5 py-3 sm:py-4",
                        "hover:bg-[#F7FAFF] hover:border-[#0B63F6]/15",
                        "transition-colors",
                      ].join(" ")}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 w-full">
                          {/* Header row */}
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap exh-tag-tight min-w-0">
                              <Text className="font-extrabold text-[#0B1B3A]">Обмен</Text>

                              <Text
                                code
                                className="max-w-[180px] sm:max-w-[320px] truncate"
                                title={x.id}
                              >
                                {x.id}
                              </Text>

                              {x.code ? (
                                <Text
                                  className="text-[12px] text-black/55"
                                  title={x.code}
                                >
                                  {x.code}
                                </Text>
                              ) : null}

                              <div className="ml-0 sm:ml-1">{statusTag(x.status)}</div>
                            </div>

                            <div className="shrink-0">
                              <div className="rounded-[12px] px-3 py-2 bg-[#F3F6FF] text-[#0B63F6] font-extrabold text-[13px] sm:text-[14px]">
                                Открыть
                              </div>
                            </div>
                          </div>

                          {/* Amounts */}
                          <div className="mt-2 flex flex-col sm:flex-row sm:items-baseline sm:gap-2 gap-1">
                            <div className="text-[18px] sm:text-[20px] font-extrabold text-[#0B1B3A] whitespace-nowrap">
                              {formatMoneyLike(x.amount_from)} {x.currency_from}
                            </div>
                            <div className="hidden sm:block text-black/40 font-extrabold">→</div>
                            <div className="text-[18px] sm:text-[20px] font-extrabold text-[#0B1B3A] whitespace-nowrap">
                              {formatMoneyLike(x.amount_to)} {x.currency_to}
                            </div>
                          </div>

                          {/* Pair/methods */}
                          <div className="mt-1 text-[12px] text-black/55">
                            {x.currency_from} · {x.method_from} → {x.currency_to} · {x.method_to}
                          </div>

                          {/* Details chips */}
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {chip("Создана", formatDate(x.created_at))}
                            {chip("Цепочка", x.exchange ? String(x.exchange).slice(0, 10) + "…" : null)}
                            {chip(
                              "Реквизит",
                              x.payout_detail
                                ? `${x.payout_type || "—"} · ${x.payout_detail}`
                                : null
                            )}
                            {chip(
                              "Курс buy/sell",
                              x.buy_rate || x.sell_rate
                                ? `${x.buy_rate ?? "—"} / ${x.sell_rate ?? "—"}`
                                : null
                            )}
                          </div>

                          {/* Small hint line */}
                          <div className="mt-2 text-[11px] text-black/45">
                            Открытие ведёт на управление сделкой (Flow).
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}